import React, { useState, useEffect } from 'react';
import { authAPI } from '../../api/client';
import { useAuth } from '../../hooks/useAuth';
import OTPVerification from './OTPVerification';
import {
    trackLoginPageView,
    trackOtpSendClick,
    trackOtpSentSuccess,
    trackLoginError,
} from '../../utils/analytics';

const Login = () => {
    const [email, setEmail] = useState('');
    const [showOTP, setShowOTP] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const { login } = useAuth();

    useEffect(() => {
        trackLoginPageView();
    }, []);

    const handleSendOTP = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        trackOtpSendClick();

        try {
            await authAPI.sendOTP(email);
            trackOtpSentSuccess();
            setShowOTP(true);
        } catch (err) {
            const errorMsg = err.response?.data?.error || 'Failed to send OTP';
            trackLoginError('otp_send_failed');
            setError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOTP = async (otp) => {
        try {
            const response = await authAPI.verifyOTP(email, otp);
            login(response.data.token, response.data.user);
        } catch (err) {
            throw new Error(err.response?.data?.error || 'Invalid OTP');
        }
    };

    if (showOTP) {
        return (
            <OTPVerification
                email={email}
                onVerify={handleVerifyOTP}
                onBack={() => setShowOTP(false)}
            />
        );
    }

    return (
        <div style={styles.container}>
            <style>{`
        .login-box {
          background: var(--card-bg);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          padding: 48px;
          max-width: 480px;
          width: 100%;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
        }
        
        .login-header {
          text-align: center;
          margin-bottom: 40px;
        }
        
        .login-logo-section {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          margin-bottom: 12px;
          flex-wrap: wrap;
        }
        
        .login-logo-image {
          height: 48px;
          width: auto;
        }
        
        .login-title {
          font-family: "Agrandir Narrow", "Agrandir", Arial, sans-serif;
          font-size: 42px;
          font-weight: 700;
          font-style: normal;
          color: var(--text-primary);
          margin-bottom: 12px;
          line-height: 1.2;
        }
        
        .login-subtitle {
          color: var(--text-secondary);
          font-size: 15px;
          line-height: 1.6;
        }
        
        .login-form {
          margin-bottom: 32px;
        }
        
        .login-features {
          border-top: 1px solid var(--border-color);
          padding-top: 32px;
        }
        
        .features-title {
          font-size: 13px;
          margin-bottom: 16px;
          color: var(--text-primary);
          text-transform: uppercase;
          letter-spacing: 1px;
          font-weight: 600;
        }
        
        .features-list {
          list-style: none;
          padding: 0;
          margin: 0;
          color: var(--text-secondary);
          line-height: 2;
        }
        
        .login-footer {
          margin-top: 32px;
          padding-top: 24px;
          border-top: 1px solid var(--border-color);
          text-align: center;
        }
        
        .login-footer-links {
          display: flex;
          gap: 20px;
          justify-content: center;
          margin-bottom: 12px;
        }
        
        .login-footer-link {
          color: var(--text-secondary);
          text-decoration: none;
          font-size: 13px;
          transition: color 0.2s ease;
        }
        
        .login-footer-link:hover {
          color: #6366f1;
        }
        
        .login-footer-email {
          font-size: 13px;
          color: var(--text-secondary);
        }
        
        .login-footer-email a {
          color: #6366f1;
          text-decoration: none;
          font-weight: 500;
        }
        
        .login-footer-email a:hover {
          text-decoration: underline;
        }
        
        @media (max-width: 768px) {
          .login-box {
            padding: 32px 24px;
            margin: 0 16px;
            border-radius: 16px;
          }
          
          .login-header {
            margin-bottom: 32px;
          }
          
          .login-logo-image {
            height: 40px;
          }
          
          .login-title {
            font-size: 28px;
          }
          
          .login-subtitle {
            font-size: 14px;
          }
          
          .login-features {
            padding-top: 24px;
          }
        }
        
        @media (max-width: 480px) {
          .login-box {
            padding: 24px 20px;
            margin: 0 12px;
          }
          
          .login-logo-section {
            flex-direction: column;
            gap: 8px;
          }
          
          .login-title {
            font-size: 24px;
          }
          
          .login-footer-links {
            flex-direction: column;
            gap: 8px;
          }
        }
      `}</style>

            <div className="login-box">
                <div className="login-header">
                    <div className="login-logo-section">
                        <img src="/logo.png" alt="APILoadTest.com" className="login-logo-image" />
                        <h1 className="login-title">APILoadTest.com</h1>
                    </div>
                    <p className="login-subtitle">API Load Testing Made Easy</p>
                </div>

                <form onSubmit={handleSendOTP} className="login-form">
                    {error && (
                        <div className="error-message">
                            <span>⚠️</span>
                            <span>{error}</span>
                        </div>
                    )}

                    <div className="input-group">
                        <label htmlFor="email">Email Address</label>
                        <input
                            id="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Enter your email"
                            required
                            autoFocus
                        />
                    </div>

                    <button
                        type="submit"
                        className="btn btn-primary"
                        style={{ width: '100%' }}
                        disabled={loading}
                    >
                        {loading ? 'Sending OTP...' : 'Send OTP'}
                    </button>
                </form>

                <div className="login-features">
                    <h3 className="features-title">Why APILoadTest.com?</h3>
                    <ul className="features-list">
                        <li>✨ Easy-to-use interface</li>
                        <li>📊 Detailed analytics</li>
                        <li>⚡ Fast and reliable</li>
                    </ul>
                </div>

                <div className="login-footer">
                    <div className="login-footer-links">
                        <a
                            href="https://docs.google.com/document/d/13xfhZnRwRMnubtFWpiB5t1rbiRSPTeXCoyGjYCPqo6A/edit?usp=sharing"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="login-footer-link"
                        >
                            Privacy Policy
                        </a>
                        <a href="mailto:admin@apiloadtest.com" className="login-footer-link">
                            Contact Us
                        </a>
                    </div>
                    <p className="login-footer-email">
                        Support: <a href="mailto:admin@apiloadtest.com">admin@apiloadtest.com</a>
                    </p>
                </div>
            </div>
        </div>
    );
};

const styles = {
    container: {
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        background: 'var(--light-gray)',
    },
};

export default Login;

